import { HeaderTestIds } from '@trello/test-ids';
import React, {
  Suspense,
  FunctionComponent,
  useCallback,
  useState,
} from 'react';
import { AddIcon } from '@trello/nachos/icons/add';
import { HeaderButton } from './HeaderButton';
import { usePopover, Popover, PopoverScreen } from '@trello/nachos/popover';
import { forNamespace, forTemplate } from '@trello/i18n';
import classnames from 'classnames';
import { useLazyComponent } from '@trello/use-lazy-component';
import { Analytics, getScreenFromUrl } from '@trello/atlassian-analytics';
import styles from './CreateMenuButton.less';
import { ScreenBreakpoints } from 'app/src/components/Responsive';
import Media from 'react-media';
import { useWorkspace } from '@trello/workspaces';
import {
  CreateBoardPopoverScreenId,
  CreateBoardPopoverScreens,
  screenAnalyticsNames,
} from 'app/src/components/CreateBoardPopover';

const viewTitle = forNamespace('view title');
const formatHeaderUser = forTemplate('header_user');

enum Screen {
  // set this initial enum value very high to avoid clashing with CreateBoardPopoverScreenId
  Menu = 100,
  CreateEnterpriseTeam,
  CreateWorkspaceView,
}

export const CreateMenuButton: FunctionComponent = () => {
  const workspace = useWorkspace();

  const {
    triggerRef,
    toggle,
    hide,
    popoverProps,
    push,
    pop,
  } = usePopover<HTMLButtonElement>({
    initialScreen: Screen.Menu,
  });

  const { currentScreen, isVisible: isPopoverVisible, onBack } = popoverProps;

  const onClick = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'createMenuHeaderButton',
      source: getScreenFromUrl(),
    });
    toggle();
  }, [toggle]);

  const handleHide = useCallback(() => {
    if (currentScreen !== undefined && screenAnalyticsNames[currentScreen]) {
      Analytics.sendClosedComponentEvent({
        componentType: 'inlineDialog',
        componentName: screenAnalyticsNames[currentScreen],
        source: 'createMenuScreen',
      });
    }
    hide();
  }, [currentScreen, hide]);

  const handleBack = useCallback(() => {
    if (currentScreen !== undefined && screenAnalyticsNames[currentScreen]) {
      Analytics.sendClickedButtonEvent({
        buttonName: 'inlineDialogBackButton',
        source: screenAnalyticsNames[currentScreen],
      });
    }
    onBack ? onBack() : pop();
  }, [currentScreen, onBack, pop]);

  const HeaderCreateMenuPopover = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "create-menu-popover" */ 'app/src/components/HeaderCreateMenuPopover'
      ),
    { namedImport: 'HeaderCreateMenuPopover' },
  );

  const CreateWorkspaceViewPopoverScreenContent = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "create-workspace-view-popover-content" */ 'app/src/components/CreateWorkspaceViewPopoverScreen/CreateWorkspaceViewPopoverScreenContent'
      ),
    {
      preload: isPopoverVisible,
      namedImport: 'CreateWorkspaceViewPopoverScreenContent',
    },
  );

  const CreateTeamPopover = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "create-team-popover" */ 'app/src/components/CreateTeamPopover'
      ),
    {
      preload: isPopoverVisible,
      namedImport: 'CreateTeamPopover',
    },
  );

  const CreateWorkspaceOverlay = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "create-team-overlay" */ 'app/src/components/CreateTeamOverlay'
      ),

    {
      preload: false,
      namedImport: 'CreateTeamOverlay',
    },
  );

  const [showCreateWorkspaceOverlay, setShowCreateWorkspaceOverlay] = useState(
    false,
  );

  const [createTeamType, setCreateTeamType] = useState<
    'default' | 'enterprise'
  >('default');

  const closeOverlay = useCallback(() => {
    setShowCreateWorkspaceOverlay(false);
  }, [setShowCreateWorkspaceOverlay]);

  const onClickCreateTeam = useCallback(
    (teamType: 'default' | 'enterprise') => {
      setCreateTeamType(teamType);
      setShowCreateWorkspaceOverlay(true);
    },
    [setShowCreateWorkspaceOverlay],
  );

  const handleOnClickCreateBoard = useCallback(() => {
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'menuItem',
      actionSubjectId: 'createBoardMenuItem',
      source: 'createMenuInlineDialog',
    });
    push(CreateBoardPopoverScreenId.CreateBoard);
  }, [push]);

  const handleOnClickCreateEnterpriseTeam = useCallback(() => {
    push(Screen.CreateEnterpriseTeam);
  }, [push]);

  const handleOnClickCreateWorkspaceView = useCallback(() => {
    push(Screen.CreateWorkspaceView);
  }, [push]);

  const handleOnClickCreateBoardFromTemplate = useCallback(() => {
    push(CreateBoardPopoverScreenId.SelectTemplate);
  }, [push]);

  const handleOnCreateTeamOverlayShown = useCallback(() => {
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'menuItem',
      actionSubjectId: 'createFreeTeamMenuItem',
      source: 'createMenuInlineDialog',
    });
    hide();
    onClickCreateTeam('default');
  }, [hide, onClickCreateTeam]);

  return (
    <>
      <Media query={ScreenBreakpoints.MediumLargeMin}>
        {(matches: boolean) => {
          return (
            <>
              <div className={styles.createMenuWrapper}>
                <HeaderButton
                  icon={matches ? undefined : <AddIcon />}
                  onClick={onClick}
                  ref={triggerRef}
                  testId={HeaderTestIds.CreateMenuButton}
                  ariaLabel={formatHeaderUser('create-board-or-team')}
                  className={classnames(styles.createMenu, {
                    [styles.createMenuActive]: isPopoverVisible,
                  })}
                >
                  {matches && (
                    <p className={styles.buttonText}>
                      {formatHeaderUser('create-menu')}
                    </p>
                  )}
                </HeaderButton>
              </div>
              <Popover
                {...popoverProps}
                title={viewTitle('create')}
                testId={HeaderTestIds.CreateMenuPopover}
                onHide={handleHide}
                onBack={handleBack}
              >
                <Suspense fallback={null}>
                  <PopoverScreen id={Screen.Menu} noHorizontalPadding>
                    <HeaderCreateMenuPopover
                      idWorkspace={workspace?.idWorkspace ?? undefined}
                      onClickCreateBoard={handleOnClickCreateBoard}
                      onClickCreateEnterpriseTeam={
                        handleOnClickCreateEnterpriseTeam
                      }
                      onClickCreateWorkspaceView={
                        handleOnClickCreateWorkspaceView
                      }
                      onClickCreateBoardFromTemplate={
                        handleOnClickCreateBoardFromTemplate
                      }
                      onCreateTeamOverlayShown={handleOnCreateTeamOverlayShown}
                    />
                  </PopoverScreen>
                  <CreateBoardPopoverScreens
                    hide={handleHide}
                    isVisible={isPopoverVisible}
                    push={push}
                  />
                  <PopoverScreen id={Screen.CreateWorkspaceView}>
                    <CreateWorkspaceViewPopoverScreenContent
                      idOrganization={workspace?.idWorkspace ?? undefined}
                      location="GlobalHeader"
                    />
                  </PopoverScreen>
                  <PopoverScreen
                    id={Screen.CreateEnterpriseTeam}
                    title={viewTitle('create organization')}
                  >
                    <CreateTeamPopover teamType="enterprise" />
                  </PopoverScreen>
                </Suspense>
              </Popover>
              {showCreateWorkspaceOverlay && (
                <Suspense fallback={null}>
                  <CreateWorkspaceOverlay
                    teamType={createTeamType}
                    onClose={closeOverlay}
                  />
                </Suspense>
              )}
            </>
          );
        }}
      </Media>
    </>
  );
};
